import { zodResolver } from '@hookform/resolvers/zod';
import { FC, PropsWithChildren, useState } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import Button from '~/components/buttons/NewButton';
import CountryController from '~/components/controllers/CountryController';
import TextController from '~/components/controllers/TextController/TextController';
import { HStack } from '~/components/layout/Stack';
import Modal from '~/components/modals/Modal/Modal';
import ModalActions from '~/components/modals/Modal/ModalActions';
import ModalContent from '~/components/modals/Modal/ModalContent';
import ModalTitle from '~/components/modals/Modal/ModalTitle';
import { snack } from '~/hooks/ToastProvider';
import { useActiveCompany } from '~/hooks/useActiveCompany';
import { useAnalytics } from '~/hooks/useAnalytics';
import useExceptionLogger from '~/hooks/useExceptionLogger';
import IconInstitution from '~/icons/icon-building.svg?react';
import IconPlus from '~/icons/icon-plus.svg?react';
import IconStore from '~/icons/icon-store.svg?react';
import IconBusinessman from '~/icons/icon-user-business.svg?react';
import IconContractor from '~/icons/icon-user-contractor.svg?react';
import { ModalProps } from '~/modals/Modal';
import { AnalyticsEvents } from '~/services/analytics';
import { useSearchCompanyIdentity } from '~/services/company-identity';
import { useCreateCounterpart } from '~/services/counterparts';
import { Company } from '~/types/company';
import { CompanyIdentity, IdentityType } from '~/types/company-identity';
import { Counterpart } from '~/types/counterparts';
import { countryIsoCodes } from '~/types/country';
import { JSONApiErrorResponse } from '~/types/jsonapi';
import { defaultDestinationCode } from '~/utils/counterparts';
import { isAxiosError } from '~/utils/helpers';
import { SDI_code } from '~/utils/invoicing';
import { firstJSONApiError } from '~/utils/jsonapi-errors';
import { isItalianTaxNumber, isItalianVatNumber } from '~/utils/validation';
import { CounterpartCreate, CounterpartFormValues } from './CounterpartEditorForm';
import styles from './NewCounterpartModal.module.scss';

const searchCounterpartSchema = z
  .object({
    country: z.enum(countryIsoCodes),
    taxNumber: z.string(),
    vatNumber: z.string(),
    identityType: z.nativeEnum(IdentityType).optional(),
  })
  .refine((val) => (val.country === 'IT' ? val.taxNumber.length > 0 : true), {
    params: { issue: 'required' },
    path: ['taxNumber'],
  })
  .refine(
    (val) => (val.country === 'IT' ? isItalianTaxNumber(val.taxNumber) || isItalianVatNumber(val.taxNumber) : true),
    { params: { issue: 'invalid_tax_number' }, path: ['taxNumber'] },
  );

type SearchCounterpartForm = z.infer<typeof searchCounterpartSchema>;

type SearchCounterpartCloseModal =
  | { action: 'CLOSE' }
  | { action: 'FOUND'; identity: CompanyIdentity }
  | { action: 'NOT_FOUND'; form: SearchCounterpartForm }
  | { action: 'INSERTING'; form: SearchCounterpartForm }
  | { action: 'ADD_COMPANY_ITSELF' };

interface SearchCounterpartProps extends ModalProps {
  closeModal: (updated?: SearchCounterpartCloseModal) => void;
}

const IdentityTypeButton: FC<PropsWithChildren<{ value: IdentityType }>> = ({ children, value }) => {
  const { watch, setValue } = useFormContext<SearchCounterpartForm>();
  const identityType = watch('identityType');
  return (
    <AriaButton
      className={styles.identityButton}
      data-selected={identityType === value}
      onPress={() => setValue('identityType', value)}
    >
      {children}
    </AriaButton>
  );
};

const SearchCounterpart = ({ closeModal }: SearchCounterpartProps) => {
  const { t } = useTranslation('counterparts');
  const { captureException } = useExceptionLogger();

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      closeModal();
    }
  };

  const searchCompany = useSearchCompanyIdentity();

  const form = useForm<SearchCounterpartForm>({
    defaultValues: { country: 'IT', taxNumber: '', vatNumber: '' },
    resolver: zodResolver(searchCounterpartSchema),
  });
  const { control, formState, handleSubmit, watch } = form;

  const identityType = watch('identityType');
  const country = watch('country');

  const nextSearch = country === 'IT' && identityType !== IdentityType.Person;

  const submit = async (values: SearchCounterpartForm) => {
    if (values.country !== 'IT' || values.identityType === IdentityType.Person) {
      closeModal({ action: 'INSERTING', form: values });
      return;
    }
    try {
      const { data: identity } = await searchCompany.mutateAsync({
        country: values.country,
        taxNumber: values.taxNumber,
      });
      closeModal({ action: 'FOUND', identity });
    } catch (error) {
      if (isAxiosError<{}>(error) && error.response?.status === 404) {
        closeModal({ action: 'NOT_FOUND', form: values });
      } else {
        closeModal({ action: 'NOT_FOUND', form: values });
        captureException(error);
        snack.error(t('modal.fail_search'));
      }
    }
  };

  const handleAddCompanyItself = () => {
    closeModal({ action: 'ADD_COMPANY_ITSELF' });
  };

  return (
    <Modal className={styles.modal} isOpen maxWidth={600} onOpenChange={handleOpenChange}>
      <ModalTitle>{t('modal.create_title')}</ModalTitle>
      <ModalContent className={styles.content}>
        <div className={styles.identitySelection}>
          <FormProvider {...form}>
            <IdentityTypeButton value={IdentityType.Company}>
              <IconStore />
              {t('identities.company')}
            </IdentityTypeButton>
            <IdentityTypeButton value={IdentityType.Pa}>
              <IconInstitution />
              {t('identities.PA')}
            </IdentityTypeButton>
            <IdentityTypeButton value={IdentityType.Contractor}>
              <IconContractor />
              {t('identities.contractor')}
            </IdentityTypeButton>
            <IdentityTypeButton value={IdentityType.Person}>
              <IconBusinessman />
              {t('identities.person')}
            </IdentityTypeButton>
          </FormProvider>
        </div>
        {!identityType && (
          <div>
            <Button variant="link" size="small" Icon={IconPlus} onClick={handleAddCompanyItself}>
              {t('modal.add_company_itself')}
            </Button>
          </div>
        )}
        {identityType && (
          <HStack gap={1}>
            <CountryController
              className={styles.control}
              control={control}
              includeTaxFreeZones
              name="country"
              label={t('modal.country')}
              required
            />
            {country === 'IT' ? (
              <TextController
                className={styles.control}
                control={control}
                name="taxNumber"
                label={t('modal.tax_number')}
                required
              />
            ) : (
              <TextController
                className={styles.control}
                control={control}
                name="vatNumber"
                label={t('modal.fiscal_identifier')}
              />
            )}
          </HStack>
        )}
      </ModalContent>
      <ModalActions>
        <Button variant="outlined" onClick={() => closeModal()} disabled={formState.isSubmitting}>
          {t('modal.cancel')}
        </Button>
        <Button disabled={!formState.isDirty} loading={formState.isSubmitting} onClick={() => handleSubmit(submit)()}>
          {nextSearch ? t('modal.search') : t('modal.continue')}
        </Button>
      </ModalActions>
    </Modal>
  );
};

type NotFoundCounterpartCloseModal = { action: 'CLOSE' } | { action: 'CONTINUE' } | { action: 'BACK' };

interface NotFoundCounterpartProps extends ModalProps {
  closeModal: (updated?: NotFoundCounterpartCloseModal) => void;
}

const NotFoundCounterpart = ({ closeModal }: NotFoundCounterpartProps) => {
  const { t } = useTranslation('counterparts');

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      closeModal();
    }
  };

  return (
    <Modal isOpen onOpenChange={handleOpenChange}>
      <ModalTitle>{t('modal.create_title')}</ModalTitle>
      <ModalContent>{t('modal.not_found_question')}</ModalContent>
      <ModalActions>
        <Button variant="outlined" onClick={() => closeModal({ action: 'BACK' })}>
          {t('modal.back')}
        </Button>
        <Button onClick={() => closeModal({ action: 'CONTINUE' })}>{t('modal.continue')}</Button>
      </ModalActions>
    </Modal>
  );
};

const defaultValuesFromCompanyIdentity = (identity: CompanyIdentity): CounterpartFormValues => {
  const identityType = identity.identityType ?? IdentityType.Company;
  return {
    address: identity.address ?? '',
    addressCountry: identity.addressCountry ?? identity.country,
    bankIdentifier: '',
    city: identity.city ?? '',
    companyName: identity.companyName ?? '',
    identityType,
    country: identity.country,
    postalCode: identity.postalCode ?? '',
    taxNumber: identity.taxNumber,
    vatNumber: identity.vatNumber ?? '',
    destinationCode: defaultDestinationCode(null, identity.country, identityType),
    account: null,
    contactPerson: null,
    contactEmail: null,
    issuedCategory: undefined,
    issuedSubcategory: undefined,
    receivedCategory: undefined,
    receivedSubcategory: undefined,
    paymentDate: null,
    paymentMethod: null,
    delivery: null,
    certifiedEmail: null,
  };
};

const defaultValuesFromSearchCounterpartForm = (form: SearchCounterpartForm): CounterpartFormValues => {
  const identityType = form.identityType ?? IdentityType.Company;
  const country = form.country;

  return {
    address: '',
    addressCountry: country,
    bankIdentifier: '',
    city: '',
    companyName: '',
    identityType,
    country,
    postalCode: country === 'IT' ? '' : '00000',
    taxNumber: country === 'IT' ? form.taxNumber : '',
    vatNumber: country !== 'IT' ? form.vatNumber : '',
    destinationCode: defaultDestinationCode(null, country, identityType),
    account: null,
    contactPerson: null,
    contactEmail: null,
    issuedCategory: undefined,
    issuedSubcategory: undefined,
    receivedCategory: undefined,
    receivedSubcategory: undefined,
    paymentDate: null,
    paymentMethod: null,
    delivery: null,
    certifiedEmail: null,
  };
};

const defaultValuesFromActiveCompany = (activeCompany: Company): CounterpartFormValues => {
  const identityType = activeCompany.companyIdentity.identityType ?? IdentityType.Company;
  return {
    address: activeCompany.companyIdentity.address ?? '',
    addressCountry: activeCompany.companyIdentity.country,
    bankIdentifier: '',
    city: activeCompany.companyIdentity.city ?? '',
    companyName: activeCompany.companyIdentity.companyName ?? '',
    identityType,
    country: activeCompany.companyIdentity.country,
    postalCode: activeCompany.companyIdentity.postalCode ?? '',
    taxNumber: activeCompany.taxNumber,
    vatNumber: activeCompany.vatNumber ?? '',
    destinationCode: defaultDestinationCode(SDI_code, activeCompany.companyIdentity.country, identityType),
    account: null,
    contactPerson: null,
    contactEmail: null,
    issuedCategory: undefined,
    issuedSubcategory: undefined,
    receivedCategory: undefined,
    receivedSubcategory: undefined,
    paymentDate: null,
    paymentMethod: null,
    delivery: null,
    certifiedEmail: null,
  };
};

const useCreateCounterpartModalAction = (closeModal: AddCounterpartModalProps['closeModal']) => {
  const { t } = useTranslation('counterparts');
  const { captureException } = useExceptionLogger();

  const createCounterpart = useCreateCounterpart();

  return ({ delivery, ...values }: CounterpartFormValues) => {
    createCounterpart.mutate(
      {
        ...values,
        issuedCategory: values.issuedCategory ?? null,
        issuedSubcategory: values.issuedSubcategory ?? null,
        receivedCategory: values.receivedCategory ?? null,
        receivedSubcategory: values.receivedSubcategory ?? null,
        account: values.account ?? null,
        deliveryAddress: delivery?.address ?? null,
        deliveryCity: delivery?.city ?? null,
        deliveryCountry: delivery?.country ?? null,
        deliveryPostalCode: delivery?.postalCode ?? null,
      },
      {
        onSuccess: (created) => closeModal({ action: 'DONE', counterpart: created }),
        onError: (error) => {
          if (
            isAxiosError<JSONApiErrorResponse>(error) &&
            error.response?.status === 422 &&
            firstJSONApiError(error)?.detail === 'has already been taken'
          ) {
            snack.error(t('modal.fail_create_already_exists'));
          } else if (
            isAxiosError<JSONApiErrorResponse>(error) &&
            error.response?.status === 422 &&
            firstJSONApiError(error)?.detail === 'deve avere al più 254 byte'
          ) {
            snack.error(t('modal.fail_create_name_too_long'));
          } else {
            captureException(error);
            snack.error(t('modal.fail_create'));
          }
        },
      },
    );
  };
};

const CreateCounterpartModal = ({
  closeModal,
  defaultValues,
}: AddCounterpartModalProps & { defaultValues: CounterpartFormValues }) => {
  const submit = useCreateCounterpartModalAction(closeModal);

  return <CounterpartCreate closeModal={() => closeModal()} defaultValues={defaultValues} submit={submit} />;
};

type CounterpartCreationState =
  | { state: 'SEARCHING' }
  | { state: 'NOT_FOUND'; searched: SearchCounterpartForm }
  | { state: 'INSERTING'; form: CounterpartFormValues };

interface AddCounterpartModalProps {
  closeModal: (updated?: { action: 'CLOSE' } | { action: 'DONE'; counterpart: Counterpart }) => void;
}

const NewCounterpartModal = ({ closeModal }: AddCounterpartModalProps) => {
  const [state, setState] = useState<CounterpartCreationState>({ state: 'SEARCHING' });
  const activeCompany = useActiveCompany();

  const searchClose = (arg?: SearchCounterpartCloseModal) => {
    if (state.state !== 'SEARCHING') throw new Error('Invalid state');
    switch (arg?.action) {
      case 'FOUND':
        setState({ state: 'INSERTING', form: defaultValuesFromCompanyIdentity(arg.identity) });
        break;

      case 'NOT_FOUND':
        setState({ state: 'NOT_FOUND', searched: arg.form });
        break;

      case 'INSERTING':
        setState({ state: 'INSERTING', form: defaultValuesFromSearchCounterpartForm(arg.form) });
        break;

      case 'ADD_COMPANY_ITSELF':
        setState({ state: 'INSERTING', form: defaultValuesFromActiveCompany(activeCompany) });
        break;

      default:
        closeModal();
    }
  };

  const notFoundClose = (arg?: NotFoundCounterpartCloseModal) => {
    if (state.state !== 'NOT_FOUND') throw new Error('Invalid state');
    switch (arg?.action) {
      case 'CONTINUE':
        setState({ state: 'INSERTING', form: defaultValuesFromSearchCounterpartForm(state.searched) });
        break;
      case 'BACK':
        setState({ state: 'SEARCHING' });
        break;
      default:
        closeModal();
    }
  };

  const { track } = useAnalytics();
  const onCloseEdit: AddCounterpartModalProps['closeModal'] = (reason) => {
    if (reason?.action === 'DONE') {
      track(AnalyticsEvents.COUNTERPART_CREATED, { context: 'document-builder' });
    }
    closeModal(reason);
  };

  switch (state.state) {
    case 'SEARCHING':
      return <SearchCounterpart closeModal={searchClose} />;
    case 'NOT_FOUND':
      return <NotFoundCounterpart closeModal={notFoundClose} />;
    case 'INSERTING':
      return <CreateCounterpartModal closeModal={onCloseEdit} defaultValues={state.form} />;
  }
};

export default NewCounterpartModal;
